import { Decimal } from 'decimal.js'
import { useMemo } from 'react'

import { NOT_APPLICABLE } from '@/configs'
import { NOT_A_NUMBER_LABEL } from '@/configs/ui'
import type { Currency } from '@/types/global'

import { getFiatCurrencyFormatOptions, getTokenQuantitiesFormatOptions, toFixed } from './'

type Locales = string | string[]

const unitSettings = [
  { suffix: 'T', unitName: 'trillion', scale: 10 ** 12 },
  { suffix: 'B', unitName: 'billion', scale: 10 ** 9 },
  { suffix: 'M', unitName: 'million', scale: 10 ** 6 },
]
const THOUSAND = 1000
const MILLION = THOUSAND * 1000
const BILLION = MILLION * 1000
const TRILLION = BILLION * 1000

enum NumberUnit {
  None = Infinity,
  Thousand = THOUSAND,
  Million = MILLION,
  Billion = BILLION,
  Trillion = TRILLION,
}

const NumberUnitMap = {
  [NumberUnit.Thousand]: ['K', 'k'],
  [NumberUnit.Million]: ['M', 'm'],
  [NumberUnit.Billion]: ['B', 'bn'],
  [NumberUnit.Trillion]: ['T', 'tn'],
}

const NUMBER_UNIT = Object.keys(NumberUnit)
  .filter(u => !isNaN(+u))
  .map(u => +u)
  .sort((a, b) => b - a)

export const getNumberWithUnit = (n: number) => {
  let unit: NumberUnit = NumberUnit.None
  let value = n
  NUMBER_UNIT.some(u => {
    if (n >= u) {
      unit = u
      value = n / u
      return true
    }
  })
  return {
    unit,
    value,
  }
}

export const parseNumberWithUnit = (n: number, unitType = 0, precision = 1) => {
  const { unit, value } = getNumberWithUnit(n)
  return {
    unit: unit === NumberUnit.None ? '' : NumberUnitMap[unit][unitType] || NumberUnitMap[unit][0],
    value: Math.floor(value * Math.pow(10, precision)) / Math.pow(10, precision),
  }
}

export const useNumberWithUnit = (n: number, u = 0, p = 1) =>
  useMemo(() => {
    const { unit, value } = parseNumberWithUnit(n, u, p)
    return `${value}${unit || ''}`
  }, [n, u, p])

export const formatNumbersWithUnit = (
  nums: Array<number | undefined>,
  unitType = 0,
  precision = 1,
) =>
  nums.map(num => {
    if (!num && num !== 0) return NOT_A_NUMBER_LABEL
    const { unit, value } = parseNumberWithUnit(num, unitType, precision)
    return `${value}${unit || ''}`
  })

export type FormatTokenPriceOptions = {
  // Show code instead if symbol empty
  symbolOrCode?: boolean
  showSymbol?: boolean
  showCode?: boolean
}

const defaultFormatPriceOptions: FormatTokenPriceOptions = {
  symbolOrCode: true,
  showSymbol: true,
  showCode: true,
}

const NUMBER_REG = /^[^\d]*(\d*(,?\d*)*.?\d*).*$/

const FRACTION_DIGITS = 2

const SIGNIFICANT_DIGITS = 4

// Cache for decimal formats
const decimalFormatCache: Record<number, Intl.NumberFormat> = {}

// Cache for effective number formats
const effectiveNumberFormatCache: Record<number, Intl.NumberFormat> = {}

/**
 * Handle undefined, NaN or null values
 * @param formatFunction - The formatting function to apply
 * @param value - The value to format
 * @param args - Additional arguments for the format function
 * @returns Formatted string or NOT_A_NUMBER_LABEL
 */
const handleNAN =
  <T extends unknown[]>(formatFunction: (v: number, ...args: T) => string) =>
  (value?: number, ...args: T): string => {
    if (!value && value !== 0) return NOT_A_NUMBER_LABEL
    return formatFunction(value as number, ...args)
  }

/**
 * Get cached decimal format
 * @param digit - Number of decimal places
 * @returns Intl.NumberFormat instance
 */
const getDecimalFormat = (digit: number): Intl.NumberFormat => {
  if (!decimalFormatCache[digit]) {
    decimalFormatCache[digit] = new Intl.NumberFormat(undefined, {
      maximumFractionDigits: digit,
      minimumFractionDigits: digit,
    })
  }
  return decimalFormatCache[digit]
}

/**
 * Get cached effective number format
 * @param digit - Number of significant digits
 * @returns Intl.NumberFormat instance
 */
const getEffectiveNumberFormat = (digit: number): Intl.NumberFormat => {
  if (!effectiveNumberFormatCache[digit]) {
    effectiveNumberFormatCache[digit] = new Intl.NumberFormat(undefined, {
      maximumSignificantDigits: digit,
    })
  }
  return effectiveNumberFormatCache[digit]
}

/**
 * Format decimal number
 * e.g.  digit = 2: 1 ==> 1.00, 1.345 ==> 1.35
 *                  1.344 ==> 1.34  1000 ==> 1,000.00
 *       digit = 0: 1 ==> 1, 1.345 ==> 1
 *                  1.344 ==> 1 1000 ==> 1,000
 * @param number - Number to format
 * @param digit - Number of decimal places
 * @param locales - Locale settings
 * @returns Formatted decimal string
 */
const _formatDecimal = (number: number, digit = 0, locales?: Locales): string => {
  if (locales) {
    return new Intl.NumberFormat(locales, {
      maximumFractionDigits: digit,
      minimumFractionDigits: digit,
    }).format(number)
  } else {
    return getDecimalFormat(digit).format(number)
  }
}

export const formatDecimal = handleNAN(_formatDecimal)

/**
 * Format effective number
 * e.g.  digit = 2: 1 ==> 1, 1.345 ==> 1.3
 *                  1.355 ==> 1.4 1234 ==> 1,200
 * @param number - Number to format
 * @param digit - Number of significant digits
 * @param locales - Locale settings
 * @returns Formatted effective number string
 */
const _formatEffectiveNumber = (number: number, digit = 4, locales?: Locales): string => {
  if (locales) {
    return new Intl.NumberFormat(locales, {
      maximumSignificantDigits: digit,
    }).format(number)
  } else {
    return getEffectiveNumberFormat(digit).format(number)
  }
}

export const formatEffectiveNumber = handleNAN(_formatEffectiveNumber)

/**
 * Format percentage
 * @param value - Value to format as percentage
 * @returns Formatted percentage string
 */
const _formatPercentage = (value: number): string => _formatDecimal(value, 2)

export const formatPercentage = handleNAN(_formatPercentage)

/**
 * Format token price in two cases(>=1 or not)
 * @param price - Price to format
 * @returns Formatted price string
 */
const _formatPrice = (price: number): string => {
  // When price >= 1, price should retain 2 decimal digits and thousands separated
  // e.g. 1 => '1.00', 123456.789 => '123,456.79'
  if (Math.abs(price) >= 1) {
    return _formatDecimal(price, FRACTION_DIGITS)
  }
  // When price < 1, there should be 4 significant digits, but, trailing 0 should be dropped
  // e.g. 0.00045678 => '0.0004568', 0.244 => '0.244'
  return _formatEffectiveNumber(price, SIGNIFICANT_DIGITS)
}

export const formatPrice = handleNAN(_formatPrice)

/**
 * Format mint price in two cases(>=1 or not)
 * Display different characters in mobile and pc current is 13 for pc 16 for mobile
 * @param price - Price to format
 * @param limitLength - Maximum length limit
 * @returns Formatted mint price string
 */
const _formatMintPrice = (price: number, limitLength: number): string => {
  // When price >= 1, price should retain 2 decimal digits and thousands separated
  // e.g. 1 => '1.00', 123456.789 => '123,456.79'
  // if the whole length is beyond limitLength , cut the digits length to fit the limit
  // if the whole length is less than limit length ,show all digits
  const priceStr = price.toString()
  if (Math.abs(price) >= 1) {
    // should cut the limited decimal places
    const intLength = priceStr.split('.')[0].length
    const hasDigits = priceStr.includes('.')
    const digitsLength = hasDigits ? priceStr.split('.')[1].length : 0
    let digitsLimitLength = FRACTION_DIGITS
    if (hasDigits && digitsLength > FRACTION_DIGITS) {
      // if digits length is too long should limit it
      if (digitsLength > limitLength - intLength - 1) {
        digitsLimitLength = limitLength - intLength - 1
      } else {
        digitsLimitLength = digitsLength
      }
    }

    return _formatDecimal(price, digitsLimitLength)
  }
  // When price < 1,
  // show all digits
  if (price.toString().length > limitLength) {
    return price.toFixed(limitLength - 2)
  }
  // there should be 4 significant digits, but, trailing 0 should be dropped
  // e.g. 0.00045678 => '0.0004568', 0.244 => '0.244'
  return _formatEffectiveNumber(price, limitLength - 2)
}

/**
 * Convert to percentage
 * @param value - Value to convert
 * @returns Formatted percentage string
 */
export const toPercentage = handleNAN((value: number) => _formatPercentage(value * 100))

/**
 * Convert to percentage with symbol
 * @param value - Value to convert
 * @returns Formatted percentage string with % symbol
 */
export const toPercentageWithSymbol = handleNAN(
  (value: number) => _formatPercentage(value * 100) + '%',
)

/**
 * Format percentage with specific rules:
 * - Total 6 numbers max, limited to 2 decimal places
 * - Over 999999% shows as "999999%"
 * - Examples: 1999999% -> 999999%, 19999.222% -> 19999.2%, 0.9999% -> 0.99%
 * @param value - Percentage value (already in percentage form, not decimal)
 * @param isPrefixPlus - Whether to prefix plus sign
 * @returns Formatted percentage string with % symbol
 */
const _formatPercentageWithPrefixLimit = (value: number, isPrefixPlus: boolean = false) => {
  if (value > 999999) {
    return '999999%'
  }

  // Convert to string to count total digits
  const absValue = Math.abs(value)
  const integerPart = Math.floor(absValue)
  const integerDigits = integerPart.toString().length

  // If integer part already has 6 digits, show no decimals
  if (integerDigits >= 6) {
    return `${isPrefixPlus && value >= 0 ? '+' : ''}${Math.round(value)}%`
  }

  const availableDecimals = Math.min(2, 6 - integerDigits)

  if (availableDecimals <= 0) {
    return `${isPrefixPlus && value >= 0 ? '+' : ''}${Math.round(value)}%`
  }

  return `${isPrefixPlus && value >= 0 ? '+' : ''}${value.toFixed(availableDecimals)}%`
}

export const formatPercentageWithPrefixLimit = handleNAN(_formatPercentageWithPrefixLimit)

/**
 * Format percentage with specific rules:
 * - Total 6 numbers max, limited to 2 decimal places
 * - Over 999999% shows as "999999%"
 * - Examples: 1999999% -> 999999%, 19999.222% -> 19999.2%, 0.9999% -> 0.99%
 * @param value - Percentage value (already in percentage form, not decimal)
 * @returns Formatted percentage string with % symbol
 */
const _formatPercentageWithLimit = (value: number): string => {
  if (value > 999999) {
    return '999999%'
  }

  // Convert to string to count total digits
  const absValue = Math.abs(value)
  const integerPart = Math.floor(absValue)
  const integerDigits = integerPart.toString().length

  // If integer part already has 6 digits, show no decimals
  if (integerDigits >= 6) {
    return `${Math.round(value)}%`
  }

  // Calculate how many decimal places we can show (max 2)
  const availableDecimals = Math.min(2, 6 - integerDigits)

  if (availableDecimals <= 0) {
    return `${Math.round(value)}%`
  }

  return `${value.toFixed(availableDecimals)}%`
}

export const formatPercentageWithLimit = handleNAN(_formatPercentageWithLimit)

/**
 * Format number with commas
 * @param value - Value to format
 * @returns Formatted number string with commas
 */
export const formatNumberWithCommas = handleNAN((value: number) => _formatDecimal(value))

/**
 * Format market cap
 * e.g.   0.00 ==> N/A, 1230 ==> 1,230
 *        100000.34 ==> 100,000, 1000000,98 ==> 1M
 * @param value - Value to format
 * @param preserveZero - Whether to preserve zero values
 * @returns Formatted market cap string
 */
export const formatMarketCap = handleNAN(
  (value: number, preserveZero = false, withBreaks = false) => {
    if (value === 0 && !preserveZero) return NOT_A_NUMBER_LABEL
    for (const { suffix, scale } of unitSettings) {
      if (value / scale >= 1) {
        let re = _formatPrice(value / scale).replace(/0+$/, '')
        if (re.endsWith('.')) re = re.slice(0, -1)

        if (withBreaks) return `${re} ${suffix}`
        return `${re}${suffix}`
      }
    }

    if (Math.abs(value) > 1) return _formatDecimal(value)

    return _formatEffectiveNumber(value, SIGNIFICANT_DIGITS)
  },
)

/**
 * Format volume (same as formatMarketCap, but will preserve zero value)
 * @param value - Value to format
 * @returns Formatted volume string
 */
export const formatVolume = handleNAN((value: number) => formatMarketCap(value, true))

/**
 * Format currency amount in the specified format.
 * There are several currency amount formats - [currencySymbol][amount][currencyCode]:
 *    '$123', '$123 USD', '123 BTC'.
 * Cryptocurrencies have no currency symbol in our site for now.
 * @param amount - Amount to format
 * @param currency - Currency object
 * @param options - Format options
 * @param {boolean} [options.symbolOrCode=true] - Display currency code if there is no currency symbol(i.e. cryptocurrency), such as '123 BTC'.
 * If true, will ignore other options
 * @param {boolean} [options.showSymbol=true] - Display currency symbol
 * @param {boolean} [options.showCode=true] - Display currency code
 * @returns Formatted currency amount
 */
export const formatCurrencyAmount = (
  amount: string | number,
  currency: Currency | null | undefined,
  options: FormatTokenPriceOptions = {},
): string => {
  if (amount === NOT_A_NUMBER_LABEL) return amount
  const { symbolOrCode, showSymbol, showCode } = {
    ...defaultFormatPriceOptions,
    ...options,
  }
  const { code: currencyCode = '', symbol: currencySymbol = '' } = currency || {}

  // If both showSymbol and showCode are false, return only the amount
  if (!showSymbol && !showCode) {
    return amount.toString()
  }

  if (symbolOrCode) {
    return `${currencySymbol}${amount}${!currencySymbol ? ' ' + currencyCode : ''}`
  }
  if (showSymbol && !showCode) {
    return `${currencySymbol}${amount}`
  }
  if (!showSymbol && showCode) {
    return `${amount} ${currencyCode}`
  }
  // Completely price format, such as: '$123 USD', '123 BTC'
  return `${currencySymbol}${amount} ${currencyCode}`
}

/**
 * Deformat currency amount
 * e.g.   $100,000,100.09 ==> 100000100.09
 * @param currencyPrice - Currency price string or number
 * @returns Deformatted number
 */
export const deFormatCurrencyAmount = (currencyPrice: string | number): number => {
  if (typeof currencyPrice === 'number') return currencyPrice
  const res = NUMBER_REG.exec(currencyPrice) || []
  let numberString = res[1]
  if (numberString) {
    numberString = numberString.replace(/,/g, '')
    return Number.isNaN(+numberString) ? 0 : Number(numberString)
  } else {
    return 0
  }
}

/**
 * rule
 * @description All types of token prices, e.g. market prices or trading prices etc.
 * @link https://mcoproduct.atlassian.net/wiki/spaces/MAT/pages/2586149191/Rounding+or+Precision+Rules+for+Token+Price+Token+Quantity+Percentages+Market+Value+Balance#Display-Token-Prices---in-BTC-(or-ETH-if-supported)-Equivalent-Price
 * e.g.  0.00112342342        ->  '0.001123'
 * e.g.  2.2342342            ->  '2.23'
 * e.g.  '16.1234560001'      ->  '16.123'
 * e.g.  12567891.231234      ->  '12,567,891.23'
 */
// TODO: rename - allTokenPricesFormat
export function fiatCurrencyFormat(
  price: string | number,
  options?: {
    separateThousands?: boolean
    showNegativeSign?: boolean
  },
) {
  try {
    const priceNum = Math.abs(Number(price))
    const { separateThousands = true } = options || {}

    const priceFormatOptions = getFiatCurrencyFormatOptions(priceNum)
    priceFormatOptions.useGrouping = separateThousands
    const numberFormat = new Intl.NumberFormat('en-US', priceFormatOptions)

    let result = numberFormat.format(priceNum)

    // Staging may have an abnormal negative number returned by the API, and the App is not handling it, showing a negative number
    if (options?.showNegativeSign && Number(price) < 0) result = '-' + result

    return removeTrailingZeros(result)
  } catch (e) {
    return NOT_APPLICABLE
  }
}

// Remove trailing 0 using a regular
export function removeTrailingZeros(numberStr: string): string {
  return numberStr.replace(/(\.\d*?[1-9])0+$/g, '$1').replace(/\.0+$/, '')
}

export function fiatCurrencyFormatNum(price: string | number): number {
  return Number(fiatCurrencyFormat(price, { separateThousands: false }))
}

export function btcPriceFormat(btcPrice: string | number) {
  const btcPriceNum = new Decimal(btcPrice)
  if (btcPriceNum.lessThan(0.000000005)) {
    return btcPriceNum.toPrecision(4)
  }
  return removeTrailingZeros(btcPriceNum.toFixed(8))
}

/**
 * @function
 * @description All token quantities. Token quantities displayed to users will be rounded using the following rule
 * @link https://mcoproduct.atlassian.net/wiki/spaces/MAT/pages/2586149191/Rounding+or+Precision+Rules+for+Token+Price+Token+Quantity+Percentages+Market+Value+Balance#Displaying-Token-Quantities
 * @param {string} amount: Token quantities
 * @return {string} Formatted
 *
 * e.g.  '0.00000000001'            ->  '0.00'
 * e.g.  '0.748452771'              ->  '0.74845277'
 * e.g.  '28.7963325'               ->  '28.7963'
 * e.g.  '453428.7915'              ->  '453,428.79'
 * e.g.  '10098943528.2964375'      ->  '10,098,943,528'
 */
export function allTokenQuantitiesFormat(
  amountStr: string,
  options?: {
    separateThousands?: boolean
    showNegativeSign?: boolean
  },
): string {
  try {
    let resultAmount = ''
    let amountFormat = Number(amountStr)
    amountFormat = Math.abs(amountFormat)

    // Rule: if quantity < 1, round down to 8 decimal places, should be rounded to 0
    if (amountFormat < 0.00000001 || amountFormat === 0) {
      return '0.00'
    }

    const { separateThousands = true } = options || {}
    const amountFormatOptions = getTokenQuantitiesFormatOptions(amountFormat)
    amountFormatOptions.useGrouping = separateThousands

    resultAmount = new Intl.NumberFormat('en-US', amountFormatOptions).format(amountFormat)

    // Staging may have an abnormal negative number returned by the API, and the App is not handling it, showing a negative number
    if (options?.showNegativeSign && Number(amountStr) < 0) resultAmount = '-' + resultAmount

    return removeTrailingZeros(resultAmount)
  } catch (e) {
    return NOT_APPLICABLE
  }
}

/**
 * @function
 * @description All currencies and balances. Displaying Market Values / Balances to users will be rounded using the following rule
 * @link https://mcoproduct.atlassian.net/wiki/spaces/MAT/pages/2586149191/Rounding+or+Precision+Rules+for+Token+Price+Token+Quantity+Percentages+Market+Value+Balance#Displaying-Market-Values-/-Balances-(Humanization)
 * @param {number | string} amount: Market Values / Balances
 * @return {string} Formatted
 *
 * e.g.  '16.1234560001'      ->  '16.12'
 * e.g.  112200111111111.23   ->  '112.20T'
 * e.g.  1234567891.231234    ->  '1.23B'
 * e.g.  12345678.12345678    ->  '12.34M'
 * e.g.  '5678.12345678'      ->  '5,678.12'
 */
export function currenciesAndBalancesHumanizedAmountFormat(
  amount: number | string,
  options?: {
    showNegativeSign?: boolean
  },
): string {
  try {
    let resultAmount = ''
    let amountFormat = Number(amount)
    amountFormat = Math.abs(amountFormat)

    if (amountFormat === 0) {
      return '0.00'
    } else if (amountFormat < 1000000) {
      resultAmount = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amountFormat)
    } else {
      const thresholds = [
        { value: 1_000_000_000_000, unit: 'T' },
        { value: 1_000_000_000, unit: 'B' },
        { value: 1_000_000, unit: 'M' },
      ]

      for (const threshold of thresholds) {
        if (amountFormat >= threshold.value) {
          const roundedValue = Math.floor((amountFormat / threshold.value) * 100) / 100
          resultAmount = `${toFixed(parseFloat(roundedValue.toString()))}${threshold.unit}`
          break
        }
      }
    }

    // Staging may have an abnormal negative number returned by the API, and the App is not handling it, showing a negative number
    if (options?.showNegativeSign && Number(amount) < 0) resultAmount = '-' + resultAmount

    return resultAmount
  } catch (e) {
    return NOT_APPLICABLE
  }
}

export function formatPercentageColor(value: number): string {
  if (value > 0) {
    return 'var(--common-success)'
  } else if (value < 0) {
    return 'var(--common-danger)'
  }
  return 'var(--content-primary)'
}
